import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DeliveryNoteList.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function DeliveryNoteList({ token }) {
  const navigate = useNavigate();
  
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchDeliveryNotes();
  }, [statusFilter]);

  const fetchDeliveryNotes = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/delivery-notes`;
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        setError('Failed to load delivery notes');
        return;
      }
      
      const data = await response.json();
      setDeliveryNotes(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError('Error loading delivery notes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (delivery_id) => {
    try {
      const response = await fetch(`${API_URL}/delivery-notes/${delivery_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to delete delivery note');
        return;
      }

      setSuccess('Delivery note deleted successfully');
      setShowDeleteConfirm(null);
      setError('');

      setTimeout(() => {
        fetchDeliveryNotes();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError('Error deleting delivery note: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = {
      'draft': 'badge-warning',
      'ready': 'badge-info',
      'in_transit': 'badge-primary',
      'delivered': 'badge-success'
    };
    return statusClass[status] || 'badge-secondary';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Draft',
      'ready': 'Ready',
      'in_transit': 'In Transit',
      'delivered': 'Delivered'
    };
    return labels[status] || status;
  };

  if (loading) {
    return <div className="loading">Loading delivery notes...</div>;
  }

  return (
    <div className="delivery-note-list-container">
      <div className="list-header">
        <div>
          <h1>Delivery Notes</h1>
          <p>Track all deliveries and manage delivery notes</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/delivery-notes/create')}
        >
          + Create Delivery Note
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="filter-section">
        <label htmlFor="status-filter">Filter by Status:</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="ready">Ready</option>
          <option value="in_transit">In Transit</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      {deliveryNotes.length === 0 ? (
        <div className="no-data">
          <p>No delivery notes found</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/delivery-notes/create')}
          >
            Create First Delivery Note
          </button>
        </div>
      ) : (
        <div className="delivery-notes-grid">
          {deliveryNotes.map(dn => (
            <div key={dn.delivery_id} className="delivery-card">
              <div className="card-header">
                <div className="card-title">
                  <h3>{dn.delivery_number}</h3>
                  <span className={`badge ${getStatusBadge(dn.status)}`}>
                    {getStatusLabel(dn.status)}
                  </span>
                </div>
              </div>

              <div className="card-body">
                <div className="detail-row">
                  <span className="label">Shesha Reference:</span>
                  <span className="value">{dn.shesha_reference || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Customer:</span>
                  <span className="value">{dn.customer_name || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Delivery Date:</span>
                  <span className="value">
                    {dn.delivery_date ? new Date(dn.delivery_date).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">City:</span>
                  <span className="value">{dn.delivery_city || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Driver:</span>
                  <span className="value">{dn.driver_name || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Truck:</span>
                  <span className="value">{dn.truck_registration || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Created:</span>
                  <span className="value">
                    {new Date(dn.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="card-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/delivery-notes/${dn.delivery_id}`)}
                >
                  View Details
                </button>
                {dn.status === 'draft' && (
                  <button
                    className="btn btn-danger"
                    onClick={() => setShowDeleteConfirm(dn.delivery_id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Delivery Note?</h3>
            <p>This delivery note will be permanently deleted. This action cannot be undone.</p>
            <div className="modal-buttons">
              <button 
                className="btn btn-danger" 
                onClick={() => handleDelete(showDeleteConfirm)}
              >
                Delete
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
